import { type FormEvent, useState } from "react"
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  PageHeader,
  RelativeTime,
  SegmentedControl,
  Spinner,
  TextArea,
  useLoad,
  useToast,
} from "../components/ui"
import { ApiError, api } from "../lib/api"

// Where this company's sessions run.
//
// Two things happen on this page and only one of them is a form. The other is
// the firewall rule: a machine that is set up perfectly and does not allow our
// address in looks identical to a machine that was never set up, so our IP is
// shown here rather than only in the installer's output.

type Placement = "shared" | "own"

type Server = {
  id: string
  name: string
  address: string | null
  arch: string
  status: string
  schedulable: boolean
  capacity: { memoryMb: number; cpus: number; diskGb: number }
  runningSessions: number
  sessionSlots: number
  lastSeenAt: string | null
  createdAt: string
}

type ServersResponse = {
  placement: Placement
  platformPublicIp: string | null
  installCommand: string
  sessionReservation: { memoryMb: number; cpus: number }
  nodes: Server[]
}

const STATUS: Record<string, { tone: "success" | "warning" | "danger" | "neutral"; label: string }> = {
  online: { tone: "success", label: "Online" },
  draining: { tone: "warning", label: "Draining" },
  offline: { tone: "danger", label: "Offline" },
  unknown: { tone: "neutral", label: "Not seen yet" },
}

export function WorkspaceServersPage() {
  const { data, loading, error, reload } = useLoad(() => api.get<ServersResponse>("/nodes"))
  const [showForm, setShowForm] = useState(false)

  return (
    <>
      <PageHeader
        title="Servers"
        subtitle="Where this company's development sessions run."
        action={
          <Button onClick={() => setShowForm(true)} disabled={showForm}>
            Add Server
          </Button>
        }
      />

      <ErrorNote error={error} />

      {loading ? (
        <Spinner />
      ) : data ? (
        <div className="flex flex-col gap-6">
          <PlacementCard data={data} onChanged={reload} />

          {showForm && (
            <AddServerForm
              data={data}
              onDone={() => {
                setShowForm(false)
                reload()
              }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {data.nodes.length ? (
            <div className="flex flex-col gap-2">
              {data.nodes.map((server) => (
                <ServerCard key={server.id} server={server} onChanged={reload} />
              ))}
            </div>
          ) : (
            <Empty
              title="No servers of your own"
              hint="Sessions run on Moderniser's servers. Add one of your own to keep your code on hardware you control."
              action={<Button onClick={() => setShowForm(true)}>Add Server</Button>}
            />
          )}
        </div>
      ) : null}
    </>
  )
}

function PlacementCard({ data, onChanged }: { data: ServersResponse; onChanged: () => void }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function change(placement: Placement) {
    if (placement === data.placement) return
    setSaving(true)
    setError(null)
    try {
      const res = await api.put<{ note: string }>("/nodes/placement", { placement })
      toast(res.note)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Where sessions run</h2>
          <p className="text-xs text-muted">Applies to new sessions. Existing ones stay where they are.</p>
        </div>
        <SegmentedControl<Placement>
          ariaLabel="Where sessions run"
          value={data.placement}
          options={[
            { value: "shared", label: "Moderniser's servers" },
            { value: "own", label: "Our own servers" },
          ]}
          onChange={(value) => void change(value)}
        />
      </div>

      <ErrorNote error={error} />

      {/* Said before it bites, not after. A customer picks their own servers so
          their code stays on hardware they control, so we never quietly place a
          session on ours — which means a full or offline fleet stops work. */}
      {data.placement === "own" && (
        <p className="rounded border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
          New sessions are placed only on your servers. If they are all full or offline, creating a
          session fails — it does not fall back to Moderniser's.
        </p>
      )}

      {saving && <p className="text-xs text-muted">Saving…</p>}
    </Card>
  )
}

function ServerCard({ server, onChanged }: { server: Server; onChanged: () => void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const status = STATUS[server.status] ?? STATUS.unknown!

  async function setSchedulable(schedulable: boolean) {
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/nodes/${server.id}`, { schedulable })
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  // Two steps on purpose. The unforced DELETE answers 409 when the machine is
  // still holding sessions, and its message names how many — asking "are you
  // sure" without that number is asking a question nobody can answer. Anything
  // that is not a 409 is a real failure and is shown rather than confirmed
  // past.
  async function remove() {
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/nodes/${server.id}`)
      toast(`${server.name} removed`)
      onChanged()
      return
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 409) {
        setError(err)
        setBusy(false)
        return
      }
      if (!window.confirm(`${err.message}\n\nRemove it anyway?`)) {
        setBusy(false)
        return
      }
    }

    try {
      await api.delete(`/nodes/${server.id}?force=true`)
      toast(`${server.name} removed`)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{server.address ?? server.name}</span>
            <Badge tone={status.tone}>{status.label}</Badge>
            {!server.schedulable && <Badge tone="neutral">Not accepting sessions</Badge>}
          </div>
          <p className="text-xs text-muted">
            {server.name} · {server.arch} · {server.capacity.cpus} CPU ·{" "}
            {server.capacity.memoryMb} MB · {server.capacity.diskGb} GB
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            variant="subtle"
            disabled={busy}
            onClick={() => void setSchedulable(!server.schedulable)}
          >
            {server.schedulable ? "Drain" : "Accept sessions"}
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => void remove()}>
            Remove
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-muted">
        <span>
          {server.runningSessions} running · room for {server.sessionSlots} more
        </span>
        {server.lastSeenAt ? (
          <span>
            Last seen <RelativeTime value={server.lastSeenAt} />
          </span>
        ) : (
          <span>Never reached</span>
        )}
      </div>

      {/* The commonest reason a machine that was set up correctly is unusable,
          and the one thing worth saying twice. */}
      {(server.status === "offline" || server.status === "unknown") && (
        <p className="text-xs text-danger">
          Moderniser cannot reach this machine. Check that your firewall allows inbound TCP 2376
          from our address, and that Docker is running.
        </p>
      )}

      <ErrorNote error={error} />
    </Card>
  )
}

function AddServerForm({
  data,
  onDone,
  onCancel,
}: {
  data: ServersResponse
  onDone: () => void
  onCancel: () => void
}) {
  const [address, setAddress] = useState("")
  const [cpus, setCpus] = useState("4")
  const [memoryMb, setMemoryMb] = useState("8192")
  const [diskGb, setDiskGb] = useState("100")
  const [credentials, setCredentials] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [copied, setCopied] = useState(false)

  const cpuCount = Number(cpus)
  const memory = Number(memoryMb)
  // What the numbers actually buy. Capacity is declared, not measured, so
  // showing the consequence is the only thing stopping someone typing 64 into a
  // 4-CPU box and wondering why sessions die.
  const slots =
    Number.isFinite(cpuCount) && Number.isFinite(memory)
      ? Math.max(
          0,
          Math.min(
            Math.floor(cpuCount / data.sessionReservation.cpus),
            Math.floor(memory / data.sessionReservation.memoryMb),
          ),
        )
      : 0

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const res = await api.post<{ reachable: boolean; reason?: string }>("/nodes", {
        address: address.trim(),
        capacityCpus: cpuCount,
        capacityMemoryMb: memory,
        capacityDiskGb: Number(diskGb),
        credentials,
      })
      // Registered either way. If we could not reach it, the reason stays on the
      // card rather than in a toast — this is something someone comes back to.
      if (!res.reachable && res.reason) window.alert(res.reason)
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="flex flex-col gap-5 p-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-foreground">1. Run this on the server</h2>
        <p className="text-xs text-muted">
          Ubuntu or Debian, as root. It installs Docker, locks it to this machine's address, issues
          certificates, and prints everything the form below needs.
        </p>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded border border-border bg-surface-muted px-3 py-2 font-mono text-xs text-foreground">
            {data.installCommand}
          </code>
          <Button
            variant="subtle"
            onClick={() => {
              void navigator.clipboard.writeText(data.installCommand)
              setCopied(true)
            }}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-foreground">2. Allow us through the firewall</h2>
        {data.platformPublicIp ? (
          <>
            <p className="text-xs text-muted">
              Moderniser connects from this address. Until it is allowed inbound on TCP 2376, the
              server will show as offline no matter how well it is set up.
            </p>
            <code className="self-start rounded border border-border bg-surface-muted px-3 py-2 font-mono text-xs text-foreground">
              {data.platformPublicIp}
            </code>
          </>
        ) : (
          <p className="rounded border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
            This deployment has no public address configured, so we cannot tell you what to allow.
            Ask Moderniser before continuing.
          </p>
        )}
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-foreground">3. Paste what it printed</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Server IP"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="203.0.113.10"
            required
          />
          <Field
            label="CPU limit"
            type="number"
            value={cpus}
            onChange={(e) => setCpus(e.target.value)}
            required
          />
          <Field
            label="Memory limit (MB)"
            type="number"
            value={memoryMb}
            onChange={(e) => setMemoryMb(e.target.value)}
            required
          />
          <Field
            label="Disk limit (GB)"
            type="number"
            value={diskGb}
            onChange={(e) => setDiskGb(e.target.value)}
            required
          />
        </div>

        <p className="text-xs text-muted">
          A session reserves {data.sessionReservation.cpus} CPU and{" "}
          {data.sessionReservation.memoryMb} MB, so these limits allow{" "}
          <strong className="text-foreground">
            {slots} session{slots === 1 ? "" : "s"}
          </strong>{" "}
          at once. Leave the operating system some room.
        </p>

        <TextArea
          label="Client certificate"
          value={credentials}
          onChange={(e) => setCredentials(e.target.value)}
          rows={6}
          required
          placeholder={"-----BEGIN MODERNISER NODE CREDENTIALS-----\n…\n-----END MODERNISER NODE CREDENTIALS-----"}
        />

        <ErrorNote error={error} />

        <div className="flex gap-2">
          <Button type="submit" disabled={saving}>
            {saving ? "Connecting…" : "Add Server"}
          </Button>
          <Button type="button" variant="subtle" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>

        <p className="text-xs text-muted">
          We connect to the machine when you press this, to read its processor type and confirm it
          answers. If your firewall rule is not in place yet, this is where you will find out.
        </p>
        <p className="text-xs text-muted">
          A new server does not take sessions immediately. Create one on it by hand first, then
          switch it to accepting sessions.
        </p>
      </form>
    </Card>
  )
}
